import type {SketchPersistentPlainData} from './SketchPersistenceTypes';

/**
 * 深度重映射透传 JSON 载荷中的对象 ID 字符串。
 *
 * 设计说明：
 * 1. `geometryData` 这类随对象保存的元数据（矩形构造线联动、槽口尺寸锚点等）内部
 *    以运行时 uuid 引用其它草图对象；重建后 uuid 必然变化，必须在持久化边界双向归一：
 *    导出时 uuid → 持久 ID，导入时 持久 ID → 新 uuid（与线性阵列扩展的 ID 归一化同范式）；
 * 2. 采用“精确字符串命中即替换”的通用深走访，不感知具体业务字段结构，
 *    未命中映射的字符串原样保留；
 * 3. 纯函数、确定性输出：相同输入与映射恒得到相同结果。
 *
 * @param data 透传 JSON 载荷。
 * @param idMap 源 ID 到目标 ID 的映射。
 * @returns 重映射后的新载荷（深拷贝，不共享引用）。
 */
export function remapSketchPersistentPlainDataIds(
    data: SketchPersistentPlainData | undefined,
    idMap: ReadonlyMap<string, string>,
): SketchPersistentPlainData {
    // 运行时快照（structuredClone 产物）可能保留显式 undefined 属性，按 JSON 语义归一为 null。
    if (data === undefined || data === null || typeof data === 'boolean' || typeof data === 'number') {
        return data ?? null;
    }
    if (typeof data === 'string') {
        return idMap.get(data) ?? data;
    }
    if (Array.isArray(data)) {
        return data.map((item) => remapSketchPersistentPlainDataIds(item, idMap));
    }
    const result: Record<string, SketchPersistentPlainData> = {};
    Object.keys(data).forEach((key) => {
        const value = (data as Record<string, SketchPersistentPlainData>)[key]!;
        /**
         * `persistentId` 字段按定义承载跨会话稳定 ID，双向重映射都不得改写：
         * 导入方向（持久 ID → 新 uuid）若改写它，会把稳定身份降级回运行时 uuid，
         * 破坏区域语义键等依赖稳定 ID 的哈希对齐。
         */
        if (key === 'persistentId' && typeof value === 'string') {
            result[key] = value;
            return;
        }
        result[key] = remapSketchPersistentPlainDataIds(value, idMap);
    });
    return result;
}
